import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useYupValidationResolver } from '~/shared/hooks/useYupValidationResolver/useYupValidationResolver';
import {
  emailValidatorSchema,
  isValidPasswordLength,
  isValidPasswordLowercaseAlphabet,
  isValidPasswordNumeric,
  isValidPasswordUppercaseAlphabet,
  passwordValidatorSchema,
} from '~/utils/validatorUtil';

/**
 * 会員登録/ログインのフォームのHooks
 */
export const useLocalState = () => {
  const signSchema = yup.object({
    email: emailValidatorSchema,
    password: passwordValidatorSchema,
    agreeTermsOfUseAndPrivacyPolicy: yup
      .string()
      .required('会員登録には利用規約、個人情報保護方針への同意が必要です。'),
  });

  const resolver = useYupValidationResolver(signSchema);
  const form = useForm<SignFormType>({
    resolver,
    defaultValues: {
      agreeTermsOfUseAndPrivacyPolicy: true,
    },
    mode: 'all',
  });
  const {
    register,
    unregister,
    errors,
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = form;

  const [onFocusEmail, setOnFocusEmail] = useState(false);

  // フォームに入力されたメールアドレス
  const watchEmailField = watch('email');
  // メールアドレスのサジェスト
  const suggestedEmailList = useMemo(() => {
    return emailSuggestion(watchEmailField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEmailField]);

  // フォームに入力されたパスワード
  const watchPasswordField = watch('password');
  const passwordValidations = useMemo(() => {
    return getPasswordValidations(watchPasswordField ?? '');
  }, [watchPasswordField]);

  // 利用規約とプライバシーポリシーへの同意
  const [agreedValue, setAgreedValue] = useState(true);
  const onClickAgreeButton = useCallback(() => {
    const nextValue = !agreedValue;
    setAgreedValue(nextValue);
    form.setValue('agreeTermsOfUseAndPrivacyPolicy', nextValue ? 'true' : '');
    form.trigger('agreeTermsOfUseAndPrivacyPolicy');
  }, [agreedValue, form]);

  useEffect(() => {
    register('agreeTermsOfUseAndPrivacyPolicy');
    return () => unregister('agreeTermsOfUseAndPrivacyPolicy');
  }, [register, unregister]);

  return {
    register,
    errors,
    handleSubmit,
    setValue,
    trigger,
    agreedValue,
    onClickAgreeButton,
    suggestedEmailList,
    passwordValidations,
    onFocusEmail,
    setOnFocusEmail,
  };
};

// オーダーのフォームの型
export type SignFormType = {
  email: string;
  password: string;
  agreeTermsOfUseAndPrivacyPolicy: boolean;
};

const getPasswordValidations = (password: string) => {
  return [
    {
      isValid: isValidPasswordLength(password),
      message: '8文字以上',
    },
    {
      isValid:
        isValidPasswordUppercaseAlphabet(password ?? '') &&
        isValidPasswordLowercaseAlphabet(password ?? ''),
      message: '小文字 (a-z) と大文字 (A-Z) を含む',
    },
    {
      isValid: isValidPasswordNumeric(password ?? ''),
      message: '数字 (0-9) を含む',
    },
  ];
};

/**
 * メールアドレスのサジェスト
 * @param email
 */
const emailSuggestion = (email: string) => {
  const suggestions = getEmailSuggests(email) ?? [];
  return suggestions.filter(suggest => suggest !== email);
};

function getEmailSuggests(email: string | undefined) {
  const emailArray = email?.split('@');
  const emailName = emailArray?.[0];
  const emailDomain = emailArray?.[1];
  if (!emailDomain) {
    return undefined;
  }

  return [...defaultDomains, ...jpDomains]
    .filter(function(domain) {
      return 0 == domain.indexOf(emailDomain);
    })
    .map(matched => {
      return `${emailName}@${matched}`;
    });
}

const defaultDomains = [
  'yahoo.com',
  'gmail.com',
  'google.com',
  'hotmail.com',
  'me.com',
  'aol.com',
  'mac.com',
  'live.com',
  'comcast.com',
  'googlemail.com',
  'msn.com',
  'hotmail.co.uk',
  'yahoo.co.uk',
  'facebook.com',
  'verizon.net',
  'att.net',
  'gmz.com',
  'mail.com',
];

const jpDomains = [
  'au.com',
  'ezweb.ne.jp',
  'uqmobile.jp',
  'docomo.ne.jp',
  'yahoo.ne.jp',
  'y-mobile.ne.jp',
  'ymobile.ne.jp',
  'disney.ne.jp',
  'i.softbank.jp',
  'softbank.ne.jp',
  'vodafone.ne.jp',
  // 楽天モバイル
  'gol.com',
  'rakuten.jp',
  // マイネオ
  'mineo.jp',
  // icloud
  'icloud.com',
];
